import { api } from '../api/endpoints'
import { deleteMeta, getMeta, metaKeys, setMeta } from '../db/meta'
import { nativePlatform } from './native'

// Push in the app goes through Apple and Google (FCM), not Web Push, which no WebView offers (native app spec, D2, D10). The
// Firebase Messaging plugin hands out an FCM token on iOS too (it registers with APNs itself), which is what the server sends to.

type PushPlugin = typeof import('@capacitor-firebase/messaging').FirebaseMessaging

// Held in a box: the plugin proxy answers `then` too, so resolving a promise with it would call FirebaseMessaging.then(), which
// does not exist.
let loaded: PushPlugin | null = null
async function push(): Promise<{ plugin: PushPlugin }> {
  loaded ??= (await import('@capacitor-firebase/messaging')).FirebaseMessaging
  return { plugin: loaded }
}


async function sendToken(token: string): Promise<void> {
  const platform = nativePlatform()
  if (!platform) return
  await api.putNativePush({ platform, token })
  await setMeta(metaKeys.nativePushToken, token)
}

/** Test seam. */
export function setPushPlugin(plugin: PushPlugin | null): void {
  loaded = plugin
  listening = false
}

let listening = false
let pendingTap: string | null = null
const tapListeners = new Set<(url: string) => void>()

/** Where a tapped notification leads: its url, else its conversation. */
export function tapTarget(data: Record<string, unknown> | undefined): string {
  const url = typeof data?.url === 'string' ? data.url : null
  if (url && url.startsWith('/') && !url.startsWith('//')) return url
  return typeof data?.channelId === 'string' ? `/c/${data.channelId}` : '/'
}

/** The registration and tap listeners, once: the token is sent to the server whenever the phone hands one out (it can change). */
async function listen(): Promise<void> {
  if (listening) return
  listening = true
  const { plugin } = await push()
  await plugin.addListener('tokenReceived', ({ token }) => void sendToken(token).catch(() => undefined))
  await plugin.addListener('notificationActionPerformed', ({ notification }) => {
    const target = tapTarget(notification.data as Record<string, unknown> | undefined)
    if (tapListeners.size === 0) pendingTap = target
    else tapListeners.forEach((l) => l(target))
  })
}

/** Opens conversations from tapped notifications; a tap that started the app is delivered once something listens. */
export function onNotificationTap(listener: (url: string) => void): () => void {
  tapListeners.add(listener)
  if (pendingTap) {
    listener(pendingTap)
    pendingTap = null
  }
  return () => tapListeners.delete(listener)
}

export type NativePushState = 'on' | 'off' | 'denied'

export async function nativePushState(): Promise<NativePushState> {
  const { receive } = await (await push()).plugin.checkPermissions()
  if (receive === 'denied') return 'denied'
  return receive === 'granted' && (await getMeta<string>(metaKeys.nativePushToken)) ? 'on' : 'off'
}

/** Asks the phone (the first time), then sends this install's token to the server. */
export async function enableNativePush(): Promise<'enabled' | 'denied'> {
  const { plugin } = await push()
  await listen()
  let { receive } = await plugin.checkPermissions()
  if (receive === 'prompt' || receive === 'prompt-with-rationale') receive = (await plugin.requestPermissions()).receive
  if (receive !== 'granted') return 'denied'
  await sendToken((await plugin.getToken()).token)
  return 'enabled'
}

/** On every start in the app: listen for taps, and send the token again when allowed (it can change between starts). */
export async function startNativePush(): Promise<void> {
  try {
    const { plugin } = await push()
    await listen()
    if ((await plugin.checkPermissions()).receive === 'granted') await sendToken((await plugin.getToken()).token)
  } catch {
    // best effort: the app works without push
  }
}

/** Switched off, or signing out: the server forgets this install's token. */
export async function disableNativePush(): Promise<void> {
  const token = await getMeta<string>(metaKeys.nativePushToken)
  if (token) await api.deleteNativePush(token).catch(() => undefined)
  await deleteMeta(metaKeys.nativePushToken)
  await (await push()).plugin.deleteToken().catch(() => undefined)
}

/** Clears the delivered notifications of a conversation (or all), as the web app does with the service worker's. */
export async function clearNativeNotifications(channelId?: string): Promise<number> {
  try {
    const { plugin } = await push()
    if (channelId === undefined) {
      await plugin.removeAllDeliveredNotifications()
      return -1
    }
    const { notifications } = await plugin.getDeliveredNotifications()
    const matching = notifications.filter((n) => (n.data as { channelId?: string } | undefined)?.channelId === channelId)
    if (matching.length) await plugin.removeDeliveredNotifications({ notifications: matching })
    return matching.length
  } catch {
    return 0
  }
}
